import {
  TabPanel as TabPanelUnstyled,
  TabsList as TabsListUnstyled,
  Tabs as TabsUnstyled,
  Tab as TabUnstyled,
} from "@mui/base";
import {
  ArrowBottomLeftIcon,
  ArrowBottomRightIcon,
  ArrowDownIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowTopLeftIcon,
  ArrowTopRightIcon,
  ArrowUpIcon,
  OpacityIcon,
} from "@radix-ui/react-icons";
import { noop } from "lodash";
import React, {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useDesignTokensStore } from "@/components/DesignTokens/store/designTokensStore";
import { resolveColor } from "@/components/DesignTokens/utils/colorUtils";
import type { CssFieldValue } from "@/components/RightStyle";
import {
  type ComboBoxOptionType,
  DiscreteInput,
  type DiscreteOption,
} from "@/components/RightStyle/TailwindStylingPanel/components/input";
import { useNewPropertyState2 } from "@/components/RightStyle/usePropertyStateV2";
import { useTailwindProperties } from "@/hooks/useTailwindProperties";
import { cn } from "@/lib/utils";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { useEditingClassesByCodebaseId } from "@/store/zustand/designCommandManagerStore";
import {
  checkArbitraryTailwindClass,
  convertOptionCssVariableToTailwindClass,
  GRADIENT_PROPERTY_TYPES,
  hasBackgroundImageClasses,
  hasGradientClasses,
  isBackgroundImageClass,
  isGradientDirectionClass,
  isSolidBackgroundClass,
  PropertyType,
} from "@/tailwind/tailwindUtils";
import { useCanvasId } from "@/utils/utils";
import useGradient, { type GradientState } from "../../hooks/useGradient";
import ColorInput from "../input/ColorInput";
import ColorSwatch from "../input/ColorSwatch";
import CustomColorPicker from "../input/CustomColorPicker";
import CustomImageUpload from "../input/CustomImageUpload";
import {
  COLOUR_FALLBACK,
  cleanBgColorValue,
  normalizeColorValue,
  parseColorValue,
} from "../input/color-picker/utils/colorFormatUtils";
import GradientColorPicker from "../input/GradientColorPicker";
import GradientControls from "../input/GradientControls";
import { VariantDraggableInput } from "../input/VariantDraggableInput";
import LibraryColorPanel from "../LibraryColorPanel";

// Add type for color option
interface ColorOption extends ComboBoxOptionType {
  value?: string;
}

interface Props {
  selectedCodebaseId?: string;
  classList?: string[];
  pickTailwindClassForProperty: (args: {
    option: ComboBoxOptionType;
    propertyType: PropertyType;
    setTemporaryDraggingValue?: (value: string | null) => void;
    variant?: string | null;
    elementClasses?: string[];
  }) => void;
  updateInstantForProperty: (args: {
    propertyType: PropertyType;
    option: ComboBoxOptionType;
    setTemporaryDraggingValue?: (value: string | null) => void;
  }) => void;
  removeClassForProperty: (args: { propertyType: PropertyType }) => void;
  removeClassesForMultipleProperties: (args: {
    propertyTypes: PropertyType[];
    setTemporaryDraggingValue?: (value: string | null) => void;
    variant?: string | null;
    thenSetOption?: ComboBoxOptionType;
    thenSetPropertyType?: PropertyType;
  }) => void;
  cssValues: { [cssName: string]: CssFieldValue };
  onOpenFillVariantModal?: () => void;
  fillVariantAnchorRef?: React.RefObject<HTMLButtonElement>;
  modalPopoverRef?: React.RefObject<HTMLDivElement>;
  onOpenFromVariantModal?: () => void;
  fromVariantAnchorRef?: React.RefObject<HTMLButtonElement>;
  onOpenViaVariantModal?: () => void;
  viaVariantAnchorRef?: React.RefObject<HTMLButtonElement>;
  onOpenToVariantModal?: () => void;
  toVariantAnchorRef?: React.RefObject<HTMLButtonElement>;
  onCloseModal?: () => void;
}

enum MainTabType {
  Custom = "custom",
  Library = "library",
}

enum ViewType {
  Fill = "fill",
  Gradient = "gradient",
  Image = "image",
}

const viewOptions: DiscreteOption[] = [
  {
    key: ViewType.Fill,
    option: {
      tailwind: "",
      css: "",
    },
    labelContent: (
      <div className="text-xs font-semibold h-6 flex items-center">Solid</div>
    ),
  },
  {
    key: ViewType.Gradient,
    option: {
      tailwind: "",
      css: "",
    },
    labelContent: (
      <div className="text-xs font-semibold h-6 flex items-center">
        Gradient
      </div>
    ),
  },
  {
    key: ViewType.Image,
    option: {
      tailwind: "",
      css: "",
    },
    labelContent: (
      <div className="text-xs font-semibold h-6 flex items-center">Image</div>
    ),
  },
];

const FillModal: React.FC<Props> = ({
  selectedCodebaseId,
  classList,
  pickTailwindClassForProperty,
  updateInstantForProperty,
  removeClassForProperty,
  removeClassesForMultipleProperties,
  cssValues,
  modalPopoverRef,
  onOpenFromVariantModal,
  fromVariantAnchorRef,
  onOpenViaVariantModal,
  viaVariantAnchorRef,
  onOpenToVariantModal,
  toVariantAnchorRef,
  onCloseModal,
}) => {
  const isDraggingRef = useRef(false);

  const { classes: elementClasses } = useEditingClassesByCodebaseId(
    classList || [],
    selectedCodebaseId || "",
  );
  const hasGradients = hasGradientClasses(elementClasses);

  const tailwindClassesMap = useCodebaseStore(
    (state) => state.tailwindClassesMap,
  );

  const canvasId = useCanvasId();

  const colorGroups = useDesignTokensStore((state) => state.colorGroups);
  const colors = useDesignTokensStore((state) => state.colors);

  const hasImageClasses = useCallback(() => {
    return hasBackgroundImageClasses(elementClasses);
  }, [elementClasses]);

  const getCurrentFillType = useCallback(() => {
    const backgroundImageValue = cssValues["background-image"];
    const hasImages = hasImageClasses();

    // Check for gradients first (classes or computed style)
    if (
      hasGradients ||
      backgroundImageValue?.evaluatedValue?.includes("gradient") ||
      backgroundImageValue?.value?.includes("gradient")
    ) {
      return ViewType.Gradient;
    }

    // Check for images
    if (
      hasImages ||
      backgroundImageValue?.evaluatedValue?.includes("url") ||
      backgroundImageValue?.value?.includes("url")
    ) {
      return ViewType.Image;
    }

    // Default to solid fill
    return ViewType.Fill;
  }, [cssValues, hasImageClasses, hasGradients]);

  const backgroundColorProperties = useTailwindProperties(
    PropertyType.BACKGROUND_COLOR,
  );

  const isColorClass = useCallback((cls: string) => {
    return isSolidBackgroundClass(cls);
  }, []);

  const isDirectionValue = useCallback((val: string) => {
    return val.startsWith("to ") || isGradientDirectionClass(val);
  }, []);

  const gradientClasses = elementClasses.filter(isGradientDirectionClass);

  const backgroundColorState = useNewPropertyState2({
    elementClasses: elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.BACKGROUND_COLOR,
    cssValues: cssValues as {
      [cssName: string]: {
        value: string;
        cssName: string;
        evaluatedValue?: string;
      };
    },
  });

  // Helper function to detect if current background color is a CSS custom property
  const isCustomColor = useCallback(() => {
    const currentClass = elementClasses.find(isColorClass);
    if (!currentClass) return false;

    // Check if it's an arbitrary class with CSS custom property
    return currentClass.includes("bg-[") && currentClass.includes("var(--");
  }, [elementClasses, isColorClass]);

  // Helper function to detect if current background color is from the library
  const isLibraryColor = useCallback(() => {
    const backgroundClasses = elementClasses.filter(
      (cls) =>
        isColorClass(cls) &&
        !checkArbitraryTailwindClass(cls, PropertyType.BACKGROUND_COLOR),
    );

    if (backgroundClasses.length === 0) return false;

    return backgroundClasses.some((currentClass) => {
      const colorpath = currentClass.startsWith("bg-")
        ? currentClass.replace(/^bg-/, "")
        : currentClass;

      // Check if this color path exists in any of the color groups
      const foundInGroups = colorGroups.some((group) =>
        group.colors.some((color) => color.tailwindClass === colorpath),
      );

      // Also check in the colors array
      const foundInColors = colors.some(
        (color) => color.tailwindClass === colorpath,
      );

      return foundInGroups || foundInColors;
    });
  }, [elementClasses, isColorClass, colorGroups, colors, tailwindClassesMap]);

  // Get the currently selected library color class (without bg- prefix)
  const getSelectedLibraryColor = useCallback((): string | undefined => {
    const backgroundClasses = elementClasses.filter(
      (cls) =>
        isColorClass(cls) &&
        !checkArbitraryTailwindClass(cls, PropertyType.BACKGROUND_COLOR),
    );

    for (const currentClass of backgroundClasses) {
      const colorpath = currentClass.startsWith("bg-")
        ? currentClass.replace(/^bg-/, "")
        : currentClass;

      // Check if this color path exists in any of the color groups
      const foundInGroups = colorGroups.some((group) =>
        group.colors.some((color) => color.tailwindClass === colorpath),
      );

      // Also check in the colors array
      const foundInColors = colors.some(
        (color) => color.tailwindClass === colorpath,
      );

      if (foundInGroups || foundInColors) {
        return colorpath;
      }
    }

    return undefined;
  }, [elementClasses, isColorClass, colorGroups, colors]);

  // Set initial tab based on color type priority: Library > Custom (CSS vars) > Custom (default)
  const [mainTab, setMainTab] = useState<MainTabType>(() => {
    // First check if it's a library color
    if (isLibraryColor()) {
      return MainTabType.Library;
    }
    // Then check if it's a CSS custom property
    if (isCustomColor()) {
      return MainTabType.Library;
    }
    // Default to Custom tab
    return MainTabType.Custom;
  });
  const [view, setView] = useState<ViewType>(getCurrentFillType);

  const backgroundImageState = useNewPropertyState2({
    elementClasses: gradientClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.BACKGROUND_IMAGE,
    cssValues: cssValues as {
      [cssName: string]: {
        value: string;
        cssName: string;
        evaluatedValue?: string;
      };
    },
  });

  const fromColorProperties = useTailwindProperties(PropertyType.FROM_COLOR);
  const toColorProperties = useTailwindProperties(PropertyType.TO_COLOR);
  const viaColorProperties = useTailwindProperties(PropertyType.VIA_COLOR);
  const fromPositionProperties = useTailwindProperties(
    PropertyType.FROM_POSITION,
  );
  const viaPositionProperties = useTailwindProperties(
    PropertyType.VIA_POSITION,
  );
  const toPositionProperties = useTailwindProperties(PropertyType.TO_POSITION);

  const fromPositionState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.FROM_POSITION,
    cssValues: cssValues as {
      [cssName: string]: {
        value: string;
        cssName: string;
        evaluatedValue?: string;
      };
    },
  });
  const viaPositionState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.VIA_POSITION,
    cssValues: cssValues as {
      [cssName: string]: {
        value: string;
        cssName: string;
        evaluatedValue?: string;
      };
    },
  });
  const toPositionState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.TO_POSITION,
    cssValues: cssValues as {
      [cssName: string]: {
        value: string;
        cssName: string;
        evaluatedValue?: string;
      };
    },
  });

  const [localImageUrl, setLocalImageUrl] = useState<string | null>(null);
  const [imageRemovedThisSession, setImageRemovedThisSession] = useState(false);

  const computedGradient = useGradient(
    cssValues["background-image"]?.evaluatedValue,
    elementClasses,
    hasGradients,
  );

  const resolveColorToHex = useCallback(
    (classes: string[]) => {
      // First, try design token resolution
      const designTokenColor = resolveColor(canvasId, classes);
      if (designTokenColor?.startsWith("#")) {
        return designTokenColor;
      }

      // Fallback to parseColorValue for each background color class
      const colorClasses = classes.filter(isColorClass).reverse();
      for (const colorClass of colorClasses) {
        if (colorClass === "bg-[]" || colorClass.match(/^bg-\[bg-\[+\]+\]$/)) {
          continue;
        }

        const parsed = parseColorValue(colorClass, backgroundColorProperties);
        if (parsed.isValid && parsed.hex) {
          return parsed.hex;
        }

        const cleaned = cleanBgColorValue(colorClass);
        if (
          cleaned &&
          (cleaned.startsWith("#") ||
            cleaned.startsWith("rgb") ||
            cleaned.startsWith("hsl"))
        ) {
          return cleaned;
        }
      }

      return null;
    },
    [resolveColor, backgroundColorProperties, isColorClass, canvasId],
  );

  const extractSolidColor = useCallback(() => {
    // Use the enhanced color resolution function
    const resolvedHex = resolveColorToHex(elementClasses);
    if (resolvedHex) {
      return resolvedHex;
    }

    // Fallback to original logic for edge cases
    const colorClasses = elementClasses.filter(isColorClass).reverse();
    for (const colorClass of colorClasses) {
      const normalized = normalizeColorValue(colorClass);
      if (normalized?.startsWith("#")) {
        return normalized;
      }
    }

    return "";
  }, [elementClasses, resolveColorToHex, isColorClass]);

  const extractGradientDirection = useCallback(() => {
    const directionClass = elementClasses.find(isDirectionValue);
    if (directionClass) {
      return directionClass;
    }
    return "to right";
  }, [elementClasses, isDirectionValue]);

  const [solidColorValue, setSolidColorValue] = useState<string>(
    extractSolidColor(),
  );
  const [localGradient, setLocalGradient] = useState<GradientState | null>(
    hasGradients && computedGradient
      ? { ...computedGradient, direction: extractGradientDirection() }
      : { direction: "to right" },
  );

  const opacityProperties = useTailwindProperties(PropertyType.OPACITY);
  const opacityState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.OPACITY,
    cssValues: cssValues as {
      [cssName: string]: {
        value: string;
        cssName: string;
        evaluatedValue?: string;
      };
    },
  });

  useEffect(() => {
    // Skip updates during dragging since the color picker is handling them
    if (isDraggingRef.current) return;

    if (backgroundColorState.propertyValue) {
      const solidColor = extractSolidColor();
      if (solidColor) {
        setSolidColorValue(cleanBgColorValue(solidColor));
      }
    } else {
      const gradientDirection = extractGradientDirection();
      if (gradientDirection) {
        setLocalGradient((prev) => ({
          ...prev,
          direction: gradientDirection,
        }));
      }
    }
  }, [
    backgroundColorState.propertyValue,
    extractSolidColor,
    extractGradientDirection,
  ]);

  const removeImageCompletely = () => {
    // Synchronously clear local image preview
    if (localImageUrl) {
      URL.revokeObjectURL(localImageUrl);
      setLocalImageUrl(null);
    }
    removeClassForProperty({ propertyType: PropertyType.BACKGROUND_IMAGE });
  };

  const removeImageIfPresent = () => {
    if (backgroundImageState.propertyValue?.includes("url(")) {
      removeImageCompletely();
    }
  };

  const handleSetSolidColor = (option: ComboBoxOptionType) => {
    // Clean up gradients and images when setting solid color using the proper property type approach
    const hasImages = hasImageClasses();

    // Use CSS value if available, otherwise fall back to tailwind value
    const cleanedValue = cleanBgColorValue(option.css || option.tailwind);

    if (hasGradients || hasImages) {
      // Clear all gradient-related property types and set the new solid color atomically
      removeClassesForMultipleProperties({
        propertyTypes: GRADIENT_PROPERTY_TYPES,
        setTemporaryDraggingValue:
          backgroundColorState.setTemporaryDraggingValue,
        thenSetOption: option,
        thenSetPropertyType: PropertyType.BACKGROUND_COLOR,
      });

      setSolidColorValue(cleanedValue);
      return;
    }

    // For simple solid color changes, pickTailwindClassForProperty handles removing existing bg- classes
    setSolidColorValue(cleanedValue);
    pickTailwindClassForProperty({
      option,
      propertyType: PropertyType.BACKGROUND_COLOR,
      setTemporaryDraggingValue: backgroundColorState.setTemporaryDraggingValue,
    });
  };

  const handleSetGradient = (
    option: ComboBoxOptionType,
    propertyType: PropertyType,
  ) => {
    // Clean up solid colors and images when setting gradient
    const hasSolidColors = elementClasses.some(isSolidBackgroundClass);
    const hasImages = elementClasses.some(isBackgroundImageClass);

    if (hasSolidColors) {
      removeClassForProperty({ propertyType: PropertyType.BACKGROUND_COLOR });
    }
    if (hasImages) {
      removeImageCompletely();
    }

    pickTailwindClassForProperty({
      option,
      propertyType,
      setTemporaryDraggingValue: backgroundImageState.setTemporaryDraggingValue,
    });
    setLocalGradient(
      computedGradient
        ? { ...computedGradient, direction: extractGradientDirection() }
        : { direction: "to right" },
    );
  };

  const buildBackgroundColorPreview = (
    option: ComboBoxOptionType,
  ): ReactNode => {
    // Use cleanBgColorValue to handle the color extraction
    let backgroundColor = COLOUR_FALLBACK;

    if (option.css) {
      const cleanedValue = cleanBgColorValue(option.css);
      backgroundColor = cleanedValue || COLOUR_FALLBACK;
    }

    return (
      <ColorSwatch
        color={backgroundColor}
        className="w-full h-full"
        size="md"
      />
    );
  };

  const backgroundImageProperties = useTailwindProperties(
    PropertyType.BACKGROUND_IMAGE,
  );

  const [gradientDirectionIcon, setGradientDirectionIcon] = useState(
    <ArrowRightIcon />,
  );

  useEffect(() => {
    if (computedGradient) {
      setLocalGradient(computedGradient);

      switch (computedGradient.direction) {
        case "to top":
          setGradientDirectionIcon(<ArrowUpIcon />);
          break;
        case "to right":
          setGradientDirectionIcon(<ArrowRightIcon />);
          break;
        case "to bottom":
          setGradientDirectionIcon(<ArrowDownIcon />);
          break;
        case "to left":
          setGradientDirectionIcon(<ArrowLeftIcon />);
          break;
        case "to top right":
        case "to right top":
          setGradientDirectionIcon(<ArrowTopRightIcon />);
          break;
        case "to top left":
        case "to left top":
          setGradientDirectionIcon(<ArrowTopLeftIcon />);
          break;
        case "to bottom right":
        case "to right bottom":
          setGradientDirectionIcon(<ArrowBottomRightIcon />);
          break;
        case "to bottom left":
        case "to left bottom":
          setGradientDirectionIcon(<ArrowBottomLeftIcon />);
          break;
        default:
          setGradientDirectionIcon(<ArrowRightIcon />);
      }
    } else {
      setLocalGradient(null);
    }
  }, [computedGradient]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: don't want to re-run this effect when the backgroundImageState changes
  useEffect(() => {
    if (localImageUrl) {
      backgroundImageState.setTemporaryDraggingValue(`url('${localImageUrl}')`);
    }

    // Cleanup function to revoke previous blob URL to prevent memory leaks
    return () => {
      if (localImageUrl) {
        URL.revokeObjectURL(localImageUrl);
      }
    };
  }, [localImageUrl]);

  const handleColorPickerPointerDown = () => {
    if (
      !imageRemovedThisSession &&
      backgroundImageState.propertyValue &&
      backgroundImageState.propertyValue.includes("url(")
    ) {
      removeImageCompletely();
      setImageRemovedThisSession(true);
    }
  };

  useEffect(() => {
    if (view !== ViewType.Fill) {
      setImageRemovedThisSession(false);
    }
  }, [view]);

  return (
    <div
      className="grid grid-cols-14 items-center gap-x-3 gap-y-2"
      ref={modalPopoverRef}
    >
      {/* Main tabs */}

      <div className="col-span-14">
        <TabsUnstyled
          value={mainTab}
          onChange={(_, value) => setMainTab(value as MainTabType)}
          className="w-full"
        >
          <TabsListUnstyled className="tab-list w-full flex font-medium text-xs p-2 pb-3 gap-2.5">
            <TabUnstyled
              className={cn(
                "p-0 text-xs font-semibold transition-colors hover:text-text-primary",
                mainTab === MainTabType.Custom
                  ? "text-secondary-900"
                  : "text-secondary-700",
              )}
              value={MainTabType.Custom}
            >
              Custom
            </TabUnstyled>
            <TabUnstyled
              className={cn(
                "p-0 text-xs font-semibold transition-colors hover:text-text-primary",
                mainTab === MainTabType.Library
                  ? "text-secondary-900"
                  : "text-secondary-700",
              )}
              value={MainTabType.Library}
            >
              Library
            </TabUnstyled>
          </TabsListUnstyled>

          {/* Library tab content */}
          <TabPanelUnstyled value={MainTabType.Library}>
            <div className="col-span-14">
              <LibraryColorPanel
                onColorSelected={(option) => {
                  handleSetSolidColor(option);
                }}
                comboboxOptions={backgroundColorProperties}
                selectedColor={getSelectedLibraryColor()}
              />
            </div>
          </TabPanelUnstyled>

          {/* Custom tab content */}
          <TabPanelUnstyled value={MainTabType.Custom}>
            {view === ViewType.Fill && (
              <div className="col-span-14">
                <CustomColorPicker
                  propertyState={{
                    ...backgroundColorState,
                    propertyValue:
                      solidColorValue ||
                      resolveColorToHex(elementClasses) ||
                      backgroundColorState.propertyValue,
                    displayValue:
                      solidColorValue ||
                      resolveColorToHex(elementClasses) ||
                      backgroundColorState.displayValue,
                  }}
                  buildArbitraryClass={(input: string) => {
                    const baseOption = {
                      tailwind: `bg-[${input}]`,
                      css: `background-color: ${input}`,
                    };
                    return convertOptionCssVariableToTailwindClass(baseOption);
                  }}
                  instantUpdateValue={(option) => {
                    isDraggingRef.current = true;
                    updateInstantForProperty({
                      propertyType: PropertyType.BACKGROUND_COLOR,
                      option,
                      setTemporaryDraggingValue:
                        backgroundColorState.setTemporaryDraggingValue,
                    });
                    const cleanedValue = cleanBgColorValue(option.css);
                    setSolidColorValue(cleanedValue);
                  }}
                  comboboxOptions={backgroundColorProperties}
                  setValue={(option) => {
                    isDraggingRef.current = false;
                    handleColorPickerPointerDown();
                    handleSetSolidColor(option);
                  }}
                  onDropdownSelected={(option) => {
                    let comboBoxOption = backgroundColorProperties.find(
                      (opt) =>
                        opt.tailwind === option.value ||
                        opt.tailwind === `bg-${option.value}`,
                    );
                    if (!comboBoxOption) {
                      comboBoxOption = {
                        tailwind:
                          typeof option.value === "string"
                            ? `bg-${option.value}`
                            : "",
                        css: "",
                        propertyType: PropertyType.BACKGROUND_COLOR,
                        ...(option as unknown as { value: string }),
                      };
                    }
                    handleSetSolidColor(comboBoxOption);
                  }}
                  onBeforeSetValue={removeImageIfPresent}
                  buildOptionPreview={buildBackgroundColorPreview}
                  tooltipContent="Background color"
                  onClear={() => {
                    removeClassForProperty({
                      propertyType: PropertyType.BACKGROUND_COLOR,
                    });
                    setSolidColorValue("");
                  }}
                  showHex={false}
                  onPointerDown={handleColorPickerPointerDown}
                />
              </div>
            )}

            {view === ViewType.Gradient && (
              <div className="col-span-14">
                <GradientColorPicker
                  classList={elementClasses}
                  gradient={localGradient || { direction: "to right" }}
                  setGradient={setLocalGradient}
                  instantUpdateValue={(option) => {
                    updateInstantForProperty({
                      propertyType: PropertyType.FROM_COLOR,
                      option,
                      setTemporaryDraggingValue:
                        backgroundImageState.setTemporaryDraggingValue,
                    });
                  }}
                  pickTailwindClassForProperty={pickTailwindClassForProperty}
                />
              </div>
            )}

            {view === ViewType.Image && (
              <CustomImageUpload
                value={
                  localImageUrl
                    ? `url('${localImageUrl}')`
                    : backgroundImageState.propertyValue
                }
                evaluatedValue={
                  localImageUrl
                    ? `url('${localImageUrl}')`
                    : backgroundImageState.propertyValue
                }
                setValue={(val) => {
                  backgroundImageState.setTemporaryDraggingValue(val);
                }}
                set={backgroundImageState.isSet}
                setSet={noop}
                buildOptionPreview={buildBackgroundColorPreview}
                buildArbitraryClass={(input: string) => {
                  const url = `url('${input}')`;
                  return {
                    tailwind: `bg-[${url}]`,
                    css: `background-image: ${url}`,
                  };
                }}
                onComboboxSelected={(option) => {
                  // Clean up solid colors and gradients when setting image
                  const hasSolidColors = elementClasses.some(
                    isSolidBackgroundClass,
                  );

                  if (hasSolidColors) {
                    removeClassForProperty({
                      propertyType: PropertyType.BACKGROUND_COLOR,
                    });
                  }
                  if (hasGradients) {
                    // Clear all gradient-related property types using the proper approach
                    removeClassesForMultipleProperties({
                      propertyTypes: GRADIENT_PROPERTY_TYPES,
                      setTemporaryDraggingValue:
                        backgroundImageState.setTemporaryDraggingValue,
                      thenSetOption: option,
                      thenSetPropertyType: PropertyType.BACKGROUND_IMAGE,
                    });
                    return;
                  }

                  pickTailwindClassForProperty({
                    option,
                    propertyType: PropertyType.BACKGROUND_IMAGE,
                    setTemporaryDraggingValue:
                      backgroundImageState.setTemporaryDraggingValue,
                  });
                }}
                onClear={() => {
                  if (localImageUrl) {
                    URL.revokeObjectURL(localImageUrl);
                    setLocalImageUrl(null);
                  }
                  removeClassForProperty({
                    propertyType: PropertyType.BACKGROUND_IMAGE,
                  });
                }}
                onLocalPreview={(url) => setLocalImageUrl(url)}
              />
            )}

            {/* Custom view type tabs - only show in Custom main tab */}
            <div className="col-span-14">
              <DiscreteInput
                className="p-0.5"
                options={viewOptions}
                onOptionSelected={(option) => {
                  setView(option.key as ViewType);
                }}
                selected={view}
                set={false}
              />
            </div>
            <div className="col-span-14 flex items-center gap-2 w-full space-y-2 vertical-center">
              {view === ViewType.Fill && (
                <div className="flex flex-row vertical-center gap-2 mt-2">
                  <div className="w-1/2">
                    <ColorInput
                      alwaysShowHex={true}
                      // don't show the context menu chevron for sub-menus
                      enableVariants={false}
                      cssValues={cssValues}
                      propertyType={PropertyType.BACKGROUND_COLOR}
                      propertyState={{
                        ...backgroundColorState,
                        propertyValue:
                          solidColorValue ||
                          resolveColorToHex(elementClasses) ||
                          backgroundColorState.propertyValue,
                        displayValue:
                          solidColorValue ||
                          resolveColorToHex(elementClasses) ||
                          backgroundColorState.displayValue,
                      }}
                      buildArbitraryClass={(input: string) => {
                        const baseOption = {
                          tailwind: `bg-[${input}]`,
                          css: `background-color: ${input}`,
                        };
                        return convertOptionCssVariableToTailwindClass(
                          baseOption,
                        );
                      }}
                      instantUpdateValue={(option) => {
                        isDraggingRef.current = true;
                        updateInstantForProperty({
                          propertyType: PropertyType.BACKGROUND_COLOR,
                          option,
                          setTemporaryDraggingValue:
                            backgroundColorState.setTemporaryDraggingValue,
                        });
                        const cleanedValue = cleanBgColorValue(option.css);
                        setSolidColorValue(cleanedValue);
                      }}
                      comboboxOptions={backgroundColorProperties}
                      setValue={(option) => {
                        isDraggingRef.current = false;
                        handleColorPickerPointerDown();
                        handleSetSolidColor(option);
                      }}
                      onDropdownSelected={(option) => {
                        let comboBoxOption = backgroundColorProperties.find(
                          (opt) =>
                            opt.tailwind === option.value ||
                            opt.tailwind === `bg-${option.value}`,
                        );
                        if (!comboBoxOption) {
                          comboBoxOption = {
                            tailwind:
                              typeof option.value === "string"
                                ? `bg-${option.value}`
                                : "",
                            css: "",
                            propertyType: PropertyType.BACKGROUND_COLOR,
                            ...(option as unknown as { value: string }),
                          };
                        }
                        handleSetSolidColor(comboBoxOption);
                      }}
                      onBeforeSetValue={removeImageIfPresent}
                      buildOptionPreview={buildBackgroundColorPreview}
                      onClear={() => {
                        removeClassForProperty({
                          propertyType: PropertyType.BACKGROUND_COLOR,
                        });
                        setSolidColorValue("");
                      }}
                      externalValue={solidColorValue}
                      pickTailwindClassForProperty={
                        pickTailwindClassForProperty
                      }
                      clearTailwindClassForProperty={(params) => {
                        removeClassForProperty({
                          propertyType: params.propertyType,
                        });
                      }}
                      updateInstantForProperty={updateInstantForProperty}
                      elementClasses={elementClasses}
                      selectedCodebaseId={selectedCodebaseId}
                      onClearVariant={onCloseModal}
                    />
                  </div>
                  <div className="w-1/2">
                    <VariantDraggableInput
                      propertyState={{
                        ...opacityState,
                        displayValue:
                          opacityState.displayValue !== undefined &&
                          opacityState.displayValue !== null
                            ? `${Number.parseFloat(
                                Number(opacityState.displayValue).toFixed(2),
                              ).toString()}`
                            : opacityState.displayValue,
                      }}
                      labelContent={<OpacityIcon />}
                      comboboxOptions={opacityProperties}
                      onComboboxSelected={(option, variant) => {
                        pickTailwindClassForProperty({
                          option,
                          propertyType: PropertyType.OPACITY,
                          setTemporaryDraggingValue:
                            opacityState.setTemporaryDraggingValue,
                          variant,
                        });
                      }}
                      onSliderValueUpdate={(option) => {
                        updateInstantForProperty({
                          propertyType: PropertyType.OPACITY,
                          option,
                          setTemporaryDraggingValue:
                            opacityState.setTemporaryDraggingValue,
                        });
                      }}
                      buildArbitraryClass={(input: string) => {
                        return {
                          tailwind: `opacity-[${input}]`,
                          css: `opacity: ${input}`,
                        };
                      }}
                      min={0}
                      max={1}
                      step={0.01}
                      tooltipContent="Opacity"
                      onClear={() => {
                        removeClassForProperty({
                          propertyType: PropertyType.OPACITY,
                        });
                      }}
                    />
                  </div>
                </div>
              )}
            </div>

            {view === ViewType.Gradient && (
              <GradientControls
                backgroundImageState={{
                  ...backgroundImageState,
                  propertyValue: hasGradients
                    ? backgroundImageState.propertyValue
                    : "to right",
                  displayValue: hasGradients
                    ? backgroundImageState.displayValue
                    : "to right",
                }}
                backgroundImageProperties={backgroundImageProperties}
                backgroundColorProperties={backgroundColorProperties}
                gradientDirectionIcon={gradientDirectionIcon}
                setValue={(option) => {
                  if (option.tailwind.startsWith("from-")) {
                    handleSetGradient(option, PropertyType.FROM_COLOR);
                  } else if (option.tailwind.startsWith("to-")) {
                    handleSetGradient(option, PropertyType.TO_COLOR);
                  } else if (option.tailwind.startsWith("via-")) {
                    handleSetGradient(option, PropertyType.VIA_COLOR);
                  } else {
                    handleSetGradient(option, PropertyType.BACKGROUND_IMAGE);
                  }
                }}
                removeTailwindClass={() => {
                  removeClassForProperty({
                    propertyType: PropertyType.BACKGROUND_IMAGE,
                  });
                }}
                classList={elementClasses}
                instantUpdateValue={(option) => {
                  updateInstantForProperty({
                    propertyType: PropertyType.BACKGROUND_IMAGE,
                    option,
                    setTemporaryDraggingValue:
                      backgroundImageState.setTemporaryDraggingValue,
                  });
                }}
                fromColorProperties={fromColorProperties}
                toColorProperties={toColorProperties}
                viaColorProperties={viaColorProperties}
                fromPositionProperties={fromPositionProperties}
                toPositionProperties={toPositionProperties}
                viaPositionProperties={viaPositionProperties}
                set={false}
                setSet={() => {}}
                onClear={() => {
                  removeClassForProperty({
                    propertyType: PropertyType.BACKGROUND_IMAGE,
                  });
                }}
                evaluatedValue={backgroundImageState.propertyValue}
                gradient={localGradient || { direction: "to right" }}
                setGradient={setLocalGradient}
                buildArbitraryClass={(currentColor, input) => {
                  const option = {
                    tailwind: "",
                    css: "",
                    propertyType: PropertyType.BACKGROUND_IMAGE,
                  };
                  switch (currentColor) {
                    case "from":
                      option.tailwind = `from-[${input}]`;
                      option.css = `--tw-gradient-from: ${input} var(--tw-gradient-from-position);`;
                      break;
                    case "via":
                      option.tailwind = `via-[${input}]`;
                      option.css = `--tw-gradient-via: ${input} var(--tw-gradient-via-position);`;
                      break;
                    case "to":
                      option.tailwind = `to-[${input}]`;
                      option.css = `--tw-gradient-to: ${input} var(--tw-gradient-to-position);`;
                      break;
                  }
                  return option;
                }}
                onOpenFromVariantModal={onOpenFromVariantModal}
                fromVariantAnchorRef={fromVariantAnchorRef}
                onOpenViaVariantModal={onOpenViaVariantModal}
                viaVariantAnchorRef={viaVariantAnchorRef}
                onOpenToVariantModal={onOpenToVariantModal}
                toVariantAnchorRef={toVariantAnchorRef}
                pickTailwindClassForProperty={pickTailwindClassForProperty}
                fromPositionState={fromPositionState}
                viaPositionState={viaPositionState}
                toPositionState={toPositionState}
              />
            )}
          </TabPanelUnstyled>
        </TabsUnstyled>
      </div>
    </div>
  );
};

export default React.memo(FillModal);
